import React, {useEffect, useState} from 'react';
import {
  Pressable,
  ScrollView,
  View,
  StyleSheet,
  ActivityIndicator,
} from 'react-native';
import CCText from '@/Atoms/CCText';
import CCLoader from '@/Atoms/CCLoader';
import BackToPrevious from '@/Atoms/BackToPrevious';
import {useClubDetail} from '@/Hooks/ClubsApi/useClubsQueries';
import {
  useJoinClub,
  useLeaveClub,
  useSetClubStatus,
} from '@/Hooks/ClubsApi/useClubsMutations';
import useLoggedInUserStore from '@/Store/loggedInUserstore';
import {useMediaQuery} from '@/Hooks/useMediaQuery/useMediaQuery';
import ClubBadge from './components/ClubBadge';
import ClubExperience from './components/ClubExperience';
import LeaderboardTab from './components/LeaderboardTab';
import ErrorBanner from '@/Components/ErrorBanner';
import {BADGES} from './badgeOptions';
import type {PieceKey} from './clubPieces';
import LeaveClubModal from './components/LeaveClubModal';

const pieceFor = (badgeId?: string): PieceKey =>
  (BADGES.find(b => b.id === badgeId)?.piece ?? 'pawn') as PieceKey;

// Club statuses a staff user can set (matches backend Club.Status enum).
const CLUB_STATUSES = [
  {value: 'active', label: 'Active'},
  {value: 'pending_review', label: 'Pending review'},
  {value: 'disabled', label: 'Disabled'},
  {value: 'banned', label: 'Banned'},
  {value: 'archived', label: 'Archived'},
];

// Avatar helpers for the member list (design uses colored circles + initials).
const AVATAR_COLORS = ['#3DAB9E', '#E17846', '#4D3F37', '#67B3E1', '#937ADB'];
const avatarColor = (name: string) =>
  AVATAR_COLORS[(name?.charCodeAt(0) ?? 0) % AVATAR_COLORS.length];
const initials = (name: string) =>
  (name ?? '')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '')
    .join('') || '?';

// Friendly copy for join/request error codes from the backend.
function joinErrorMessage(code: string): string {
  switch (code) {
    case 'min_rating':
      return "Your rating doesn't meet this club's minimum.";
    case 'club_full':
      return 'This club is full (50/50).';
    case 'not_open':
      return "This club isn't open to join right now.";
    case 'already_requested':
      return 'Your request is already pending.';
    default:
      return "Couldn't complete that. Please try again.";
  }
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#FAF6EB',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  backButton: {
    paddingVertical: 8,
    paddingHorizontal: 8,
  },
  backText: {
    color: '#4D3F37',
    fontWeight: '600',
    fontSize: 14,
  },
  scrollContent: {
    paddingHorizontal: 16,
    paddingVertical: 20,
  },
  profileScroll: {
    flex: 1,
  },
  wideScrollContent: {
    alignItems: 'center',
    paddingHorizontal: 28,
    paddingVertical: 20,
  },
  wideBackRow: {
    width: '100%',
    maxWidth: 1180,
    marginBottom: 6,
  },
  wideCard: {
    width: '100%',
    maxWidth: 1180,
    backgroundColor: '#fff',
    borderRadius: 24,
    padding: 24,
    borderWidth: 1,
    borderColor: '#E0DDD9',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.08,
    shadowRadius: 2,
    elevation: 2,
  },
  wideColumns: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 24,
  },
  wideSidebar: {
    width: 360,
    flexShrink: 0,
    padding: 6,
  },
  wideExperienceColumn: {
    flex: 1,
    minWidth: 0,
  },
  wideRules: {
    marginTop: 20,
    padding: 16,
    borderRadius: 14,
    backgroundColor: '#FAF6EB',
    borderWidth: 1,
    borderColor: '#EEE6D4',
  },
  wideLeaderboard: {
    marginTop: 20,
    marginBottom: 0,
  },
  heroSection: {
    alignItems: 'center',
    marginBottom: 24,
  },
  wideHeroRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 20,
    marginBottom: 0,
  },
  badge: {
    width: 78,
    height: 78,
    borderRadius: 39,
    backgroundColor: '#E0DDD9',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  wideBadge: {
    width: 84,
    height: 84,
    borderRadius: 22,
    marginBottom: 0,
  },
  badgeText: {
    fontSize: 32,
    fontWeight: '600',
    color: '#4D3F37',
  },
  wideBadgeText: {
    fontSize: 34,
  },
  clubName: {
    fontSize: 26,
    color: '#262322',
    textAlign: 'center',
    marginBottom: 8,
  },
  wideClubName: {
    fontSize: 28,
    textAlign: 'left',
    marginBottom: 0,
  },
  nameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 8,
  },
  wideNameRow: {
    justifyContent: 'flex-start',
    marginBottom: 0,
  },
  officialBadge: {
    backgroundColor: '#E9F3FF',
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 7,
    marginLeft: 8,
  },
  officialText: {
    color: '#2A7BB8',
    fontSize: 11,
    fontWeight: '600',
  },
  privacyLine: {
    fontSize: 13,
    color: '#888280',
    marginBottom: 12,
  },
  widePrivacyLine: {
    marginTop: 3,
    marginBottom: 0,
  },
  description: {
    fontSize: 14,
    color: '#4D3F37',
    lineHeight: 22,
    textAlign: 'center',
  },
  wideDescription: {
    fontSize: 14.5,
    lineHeight: 22.5,
    textAlign: 'left',
    marginTop: 18,
  },
  cardsContainer: {
    marginBottom: 20,
    gap: 12,
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.08,
    shadowRadius: 2,
    elevation: 2,
  },
  wideCardSection: {
    marginTop: 18,
    padding: 16,
    backgroundColor: '#FAF6EB',
    borderRadius: 12,
    marginBottom: 0,
    shadowOpacity: 0,
    elevation: 0,
    marginBottom: 20,
  },
  sectionLabel: {
    fontSize: 12,
    fontWeight: '600',
    color: '#888280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 12,
  },
  lockCard: {
    backgroundColor: '#FFF0C8',
    borderRadius: 14,
    padding: 16,
    marginBottom: 12,
    flexDirection: 'row',
  },
  wideLockCard: {
    marginTop: 18,
    marginBottom: 0,
  },
  lockIcon: {
    fontSize: 22,
    marginRight: 12,
    marginTop: 2,
  },
  lockContent: {
    flex: 1,
  },
  lockTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#262322',
    marginBottom: 4,
  },
  lockBody: {
    fontSize: 13,
    color: '#806234',
    lineHeight: 19,
  },
  topMemberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 6,
  },
  topMemberRank: {
    width: 26,
    textAlign: 'center',
    fontSize: 13,
    fontWeight: '700',
    color: '#806234',
  },
  topMemberAvatar: {
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
  },
  topMemberInitials: {
    fontSize: 12,
    fontWeight: '600',
    color: '#fff',
  },
  topMemberName: {
    flex: 1,
    fontSize: 14,
    color: '#262322',
  },
  topMemberPts: {
    fontSize: 13,
    fontWeight: '700',
    color: '#4D3F37',
  },
  topMemberRole: {
    fontSize: 10,
    fontWeight: '700',
    letterSpacing: 0.03,
    color: '#1E7A3D',
    backgroundColor: '#E4F6EB',
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 6,
    overflow: 'hidden',
  },
  bottomBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    paddingHorizontal: 16,
    paddingVertical: 22,
    backgroundColor: '#FAF6EB',
    borderTopWidth: 1,
    borderTopColor: 'rgba(0,0,0,0.05)',
  },
  joinButton: {
    width: '100%',
    borderRadius: 32,
    paddingVertical: 15,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#4D3F37',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
  },
  joinButtonText: {
    fontWeight: '600',
    fontSize: 16,
  },
  leaveClubButton: {
    alignSelf: 'center',
    marginTop: 12,
    paddingVertical: 9,
    paddingHorizontal: 18,
    borderRadius: 20,
  },
  leaveClubText: {
    color: '#A33A2D',
    fontSize: 14,
    fontWeight: '700',
  },
  requestedHint: {
    fontSize: 12,
    color: '#888280',
    textAlign: 'center',
    marginTop: 8,
  },
  ratingBanner: {
    backgroundColor: '#FFEBEE',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 13,
    marginBottom: 10,
    alignItems: 'center',
  },
  ratingBannerText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#CD2C39',
    textAlign: 'center',
  },
  minRatingChip: {
    alignSelf: 'flex-start',
    backgroundColor: '#FFEFEE',
    borderRadius: 999,
    paddingVertical: 4,
    paddingHorizontal: 11,
  },
  minRatingChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#CD2C39',
  },
  actionErrorBanner: {
    marginTop: 8,
  },
  containerWithBottom: {
    paddingBottom: 120,
  },
  statusBanner: {
    backgroundColor: '#FDECEE',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F3C9CE',
    padding: 12,
    marginBottom: 12,
  },
  statusBannerTitle: {
    fontSize: 13.5,
    fontWeight: '700',
    color: '#CD2C39',
    textTransform: 'capitalize',
    marginBottom: 4,
  },
  statusBannerText: {
    fontSize: 12.5,
    color: '#8A5A5E',
    lineHeight: 17,
  },
  staffStatusBox: {
    marginTop: 14,
    padding: 12,
    borderRadius: 12,
    backgroundColor: '#FBF3D6',
    borderWidth: 1,
    borderColor: '#F0DFA0',
  },
  staffStatusLabel: {
    fontSize: 11,
    fontWeight: '700',
    color: '#8A6D1F',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 8,
  },
  staffStatusChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  staffStatusChip: {
    paddingVertical: 7,
    paddingHorizontal: 12,
    borderRadius: 999,
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: 'rgba(38,35,34,0.15)',
  },
  staffStatusChipActive: {
    backgroundColor: '#3DAB9E',
    borderColor: '#3DAB9E',
  },
  staffStatusChipText: {
    fontSize: 12.5,
    fontWeight: '600',
    color: '#4D3F37',
  },
  staffStatusChipTextActive: {
    color: '#fff',
  },
});

export default function ClubProfile({navigation, route}: any) {
  // `?? {}` is load-bearing: this is a registered drawer screen, so it can be
  // entered with no params at all (deep link, or a back control that carried
  // only the route name). Destructuring undefined threw before render.
  const {clubId} = route?.params ?? {};
  const {data, isLoading} = useClubDetail(clubId);
  const joinClub = useJoinClub(clubId);
  const leaveClub = useLeaveClub(clubId);
  const setClubStatus = useSetClubStatus(clubId);
  const isStaff = useLoggedInUserStore((s: any) => !!s.userData?.is_staff);
  const [requested, setRequested] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [leaveError, setLeaveError] = useState('');
  const {isMobileOrTab} = useMediaQuery();
  const isWide = !isMobileOrTab;

  // No clubId means this screen can't render anything meaningful — send the
  // user to the list rather than firing a request for `undefined`.
  useEffect(() => {
    if (!clubId) {
      navigation.navigate('Clubs');
    }
  }, [clubId, navigation]);

  if (!clubId || isLoading || !data) {
    return <CCLoader loading={true} />;
  }
  const club = data.club;

  const onJoinPress = async () => {
    setError(null);
    setJoining(true);
    try {
      const res: any = await joinClub.mutateAsync();
      if (res.joined) {
        navigation.navigate('ClubRoom', {clubId});
      } else if (res.requested) {
        setRequested(true);
      }
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'unknown_error');
    } finally {
      setJoining(false);
    }
  };

  const privacyCapitalized =
    club.visibility === 'public' ? 'Public' : 'Private';

  const isMember = !!club.my_role;
  const clubMembers = club.members ?? [];
  const replacementCandidates = clubMembers.filter(
    (member: any) => member.role === 'member',
  );
  const isSoleAdmin =
    club.my_role === 'admin' &&
    clubMembers.filter((member: any) => member.role === 'admin').length === 1;
  const requiresReplacement = isSoleAdmin && club.member_count > 1;
  const isPrivate = club.visibility === 'private';
  const shouldShowLockCard = isPrivate && !isMember;

  // A single button descriptor per state, mirroring the design prototype's
  // join-state VM (open→Join, approval→Request, requested→Requested✓, invite-
  // only→Invite only, full→Club full). Colors match the design exactly.
  const isFull = club.official && club.member_count >= 50;
  const isApproval = club.join_policy === 'approval';
  const isInviteOnly = club.join_policy === 'invite_only';
  const isActive = (club.status ?? 'active') === 'active';
  // Proactive rating gate: the club has a minimum and the viewer doesn't meet it
  // (backend computes min_rating_met; falls back to comparing viewer_rating).
  const ratingTooLow =
    !isMember &&
    !!club.min_rating &&
    (club.min_rating_met === false ||
      (club.min_rating_met == null &&
        (club.viewer_rating == null || club.viewer_rating < club.min_rating)));
  const joinBtn = (() => {
    // Non-active clubs (disabled/banned/pending_review/archived) can't be joined or
    // opened by regular users. Staff keep controls via the status chips below;
    // members/others get a disabled status button + the raise-a-ticket note.
    if (!isActive && !isStaff) {
      return {
        label: `Club ${(club.status ?? '').replace('_', ' ')}`,
        bg: '#EDE6D2',
        fg: '#A8A7A6',
        disabled: true,
        action: 'none' as const,
      };
    }
    if (isMember) {
      return {
        label: 'Open club',
        bg: '#3DAB9E',
        fg: '#fff',
        disabled: false,
        action: 'open' as const,
      };
    }
    if (isFull) {
      return {
        label: 'Club full (50/50)',
        bg: '#EDE6D2',
        fg: '#A8A7A6',
        disabled: true,
        action: 'none' as const,
      };
    }
    if (isInviteOnly) {
      return {
        label: 'Invite only',
        bg: '#EDE6D2',
        fg: '#A8A7A6',
        disabled: true,
        action: 'none' as const,
      };
    }
    if (requested) {
      return {
        label: 'Requested ✓',
        bg: '#E4F6EB',
        fg: '#1E7A3D',
        disabled: true,
        action: 'none' as const,
      };
    }
    if (ratingTooLow) {
      return {
        label: 'Rating too low',
        bg: '#EDE6D2',
        fg: '#A8A7A6',
        disabled: true,
        action: 'none' as const,
      };
    }
    if (club.join_policy === 'open') {
      return {
        label: 'Join',
        bg: '#FACF47',
        fg: '#4D3F37',
        disabled: false,
        action: 'join' as const,
      };
    }
    return {
      label: 'Request to Join',
      bg: '#3DAB9E',
      fg: '#fff',
      disabled: false,
      action: 'join' as const,
    };
  })();
  const showRequestedHint =
    isApproval && !requested && !isMember && !isFull && !ratingTooLow;

  const onButtonPress = () => {
    if (joinBtn.action === 'open') {
      navigation.navigate('ClubRoom', {clubId});
    } else if (joinBtn.action === 'join') {
      onJoinPress();
    }
  };

  const confirmLeave = async (replacementAdminId?: number) => {
    setLeaveError('');
    try {
      await leaveClub.mutateAsync(replacementAdminId);
      setLeaveOpen(false);
      goToClubs();
    } catch (e: any) {
      const code = e?.response?.data?.error;
      setLeaveError(
        code === 'replacement_admin_required'
          ? 'Choose the next admin before leaving.'
          : code === 'invalid_replacement_admin'
          ? 'That member can no longer be promoted. Choose another member.'
          : "Couldn't leave the club. Please try again.",
      );
    }
  };

  const actionArea = (
    <>
      {!isActive ? (
        <View testID="club-status-banner" style={styles.statusBanner}>
          <CCText style={styles.statusBannerTitle}>
            This club is {(club.status ?? '').replace('_', ' ')}
          </CCText>
          <CCText style={styles.statusBannerText}>
            It's currently unavailable to join or open. If you believe this is a
            mistake, please raise your concern via “Raise a Ticket”.
          </CCText>
        </View>
      ) : null}
      {ratingTooLow ? (
        <View testID="rating-too-low-banner" style={styles.ratingBanner}>
          <CCText style={styles.ratingBannerText}>
            This club needs {club.min_rating}+ rating
          </CCText>
        </View>
      ) : null}
      <Pressable
        testID={isMember ? 'club-open-btn' : 'club-join-btn'}
        onPress={onButtonPress}
        disabled={joinBtn.disabled || joining}
        style={({pressed}) => [
          styles.joinButton,
          {backgroundColor: joinBtn.bg},
          pressed && !joinBtn.disabled && {transform: [{translateY: -2}]},
        ]}>
        {joining ? (
          <ActivityIndicator color={joinBtn.fg} />
        ) : (
          <CCText
            testID={requested ? 'club-requested' : undefined}
            style={[styles.joinButtonText, {color: joinBtn.fg}]}>
            {joinBtn.label}
          </CCText>
        )}
      </Pressable>
      {isMember ? (
        <Pressable
          testID="club-leave-btn"
          onPress={() => {
            setLeaveError('');
            setLeaveOpen(true);
          }}
          style={styles.leaveClubButton}>
          <CCText style={styles.leaveClubText}>Leave club</CCText>
        </Pressable>
      ) : null}
      <LeaveClubModal
        visible={leaveOpen}
        clubName={club.name}
        members={replacementCandidates}
        requiresReplacement={requiresReplacement}
        isLastMember={club.member_count === 1}
        submitting={(leaveClub as any).isLoading}
        error={leaveError}
        onClose={() => {
          if (!(leaveClub as any).isLoading) {
            setLeaveOpen(false);
          }
        }}
        onConfirm={confirmLeave}
      />
      {showRequestedHint ? (
        <CCText style={styles.requestedHint}>
          An admin will review your request. You'll get a notification.
        </CCText>
      ) : null}
      {error && error !== 'already_member' ? (
        <ErrorBanner
          testID="club-profile-error"
          message={joinErrorMessage(error)}
          style={styles.actionErrorBanner}
        />
      ) : null}
      {isStaff ? (
        <View testID="staff-status-control" style={styles.staffStatusBox}>
          <CCText style={styles.staffStatusLabel}>Staff · Club status</CCText>
          <View style={styles.staffStatusChips}>
            {CLUB_STATUSES.map(s => {
              const active = club.status === s.value;
              return (
                <Pressable
                  key={s.value}
                  testID={`club-status-${s.value}`}
                  disabled={active || (setClubStatus as any).isLoading}
                  onPress={() => (setClubStatus as any).mutate(s.value)}
                  style={[
                    styles.staffStatusChip,
                    active && styles.staffStatusChipActive,
                  ]}>
                  <CCText
                    style={[
                      styles.staffStatusChipText,
                      active && styles.staffStatusChipTextActive,
                    ]}>
                    {s.label}
                  </CCText>
                </Pressable>
              );
            })}
          </View>
        </View>
      ) : null}
    </>
  );

  if (isWide) {
    return (
      <View style={styles.container}>
        <ScrollView
          style={styles.profileScroll}
          contentContainerStyle={styles.wideScrollContent}>
          <View style={styles.wideBackRow}>
            {/* Names wherever you came from. ClubRoom is excluded: it
                redirects non-active clubs here on mount, so offering it as the
                way back would bounce you straight in again. */}
            <BackToPrevious
              navigation={navigation}
              exclude={['ClubRoom', 'ClubProfile']}
            />
          </View>
          <View style={styles.wideCard}>
            <View style={styles.wideColumns}>
              <View style={styles.wideSidebar}>
                <View style={styles.wideHeroRow}>
                  <ClubBadge
                    piece={pieceFor(club.badge_id)}
                    color={club.background_color || '#3DAB9E'}
                    size={84}
                    radius={22}
                  />
                  <View style={{flex: 1}}>
                    <View style={[styles.nameRow, styles.wideNameRow]}>
                      <CCText
                        fontWeight="thunder"
                        style={[styles.clubName, styles.wideClubName]}>
                        {club.name}
                      </CCText>
                      {club.official ? (
                        <View style={styles.officialBadge}>
                          <CCText style={styles.officialText}>OFFICIAL</CCText>
                        </View>
                      ) : null}
                    </View>
                    <CCText
                      style={[styles.privacyLine, styles.widePrivacyLine]}>
                      {privacyCapitalized} · {club.member_count} members
                    </CCText>
                    {club.min_rating ? (
                      <View style={[styles.minRatingChip, {marginTop: 8}]}>
                        <CCText style={styles.minRatingChipText}>
                          Min rating {club.min_rating}
                        </CCText>
                      </View>
                    ) : null}
                  </View>
                </View>

                {club.description ? (
                  <CCText style={[styles.description, styles.wideDescription]}>
                    {club.description}
                  </CCText>
                ) : null}

                {club.members && club.members.length > 0 ? (
                  <View
                    style={[styles.wideCardSection, styles.wideLeaderboard]}>
                    <CCText style={styles.sectionLabel}>
                      This week's top members
                    </CCText>
                    <LeaderboardTab
                      clubId={clubId}
                      variant="overview"
                      limit={5}
                      navigation={navigation}
                    />
                  </View>
                ) : null}

                {shouldShowLockCard ? (
                  <View style={[styles.lockCard, styles.wideLockCard]}>
                    <CCText style={styles.lockIcon}>🔒</CCText>
                    <View style={styles.lockContent}>
                      <CCText style={styles.lockTitle}>Private club</CCText>
                      <CCText style={styles.lockBody}>
                        Members and chat are hidden until you join.
                      </CCText>
                    </View>
                  </View>
                ) : null}

                <View style={styles.wideRules}>
                  <CCText style={styles.sectionLabel}>Club rules</CCText>
                  <CCText
                    style={{
                      fontSize: 13.5,
                      color: '#4D3F37',
                      lineHeight: 19,
                    }}>
                    {club.rules}
                  </CCText>
                </View>

                <View style={{marginTop: 18}}>{actionArea}</View>
              </View>

              <View style={styles.wideExperienceColumn}>
                <ClubExperience
                  isWide
                  isMember={isMember}
                  memberCount={club.member_count}
                />
              </View>
            </View>
          </View>
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <BackToPrevious
          navigation={navigation}
          exclude={['ClubRoom', 'ClubProfile']}
        />
      </View>

      <ScrollView
        style={styles.profileScroll}
        contentContainerStyle={[
          styles.scrollContent,
          styles.containerWithBottom,
          isMember && {paddingBottom: 180},
          isStaff && {paddingBottom: 280},
        ]}>
        {/* Hero section */}
        <View style={styles.heroSection}>
          <View style={{marginBottom: 16}}>
            <ClubBadge
              piece={pieceFor(club.badge_id)}
              color={club.background_color || '#3DAB9E'}
              size={78}
              radius={22}
            />
          </View>

          <View style={styles.nameRow}>
            <CCText fontWeight="thunder" style={styles.clubName}>
              {club.name}
            </CCText>
            {club.official ? (
              <View style={styles.officialBadge}>
                <CCText style={styles.officialText}>OFFICIAL</CCText>
              </View>
            ) : null}
          </View>

          <CCText style={styles.privacyLine}>
            {privacyCapitalized} · {club.member_count} members
          </CCText>

          {club.min_rating ? (
            <View
              style={[
                styles.minRatingChip,
                {alignSelf: 'center', marginBottom: 12},
              ]}>
              <CCText style={styles.minRatingChipText}>
                Min rating {club.min_rating}
              </CCText>
            </View>
          ) : null}

          {club.description ? (
            <CCText style={styles.description}>{club.description}</CCText>
          ) : null}
        </View>

        {/* Cards section */}
        <View style={styles.cardsContainer}>
          <ClubExperience isMember={isMember} memberCount={club.member_count} />

          {/* Weekly top five members */}
          {club.members && club.members.length > 0 ? (
            <View style={styles.card}>
              <CCText style={styles.sectionLabel}>
                This week's top members
              </CCText>
              <LeaderboardTab clubId={clubId} variant="overview" limit={5} navigation={navigation} />
            </View>
          ) : null}

          {/* Club rules card */}
          <View style={styles.card}>
            <CCText style={styles.sectionLabel}>Club rules</CCText>
            <CCText style={{fontSize: 13, color: '#4D3F37', lineHeight: 19}}>
              {club.rules}
            </CCText>
          </View>

          {/* Private club lock note - only show when private and not a member */}
          {shouldShowLockCard ? (
            <View style={styles.lockCard}>
              <CCText style={styles.lockIcon}>🔒</CCText>
              <View style={styles.lockContent}>
                <CCText style={styles.lockTitle}>Private club</CCText>
                <CCText style={styles.lockBody}>
                  Members and chat are hidden until you join. Request to join
                  and an admin will review.
                </CCText>
              </View>
            </View>
          ) : null}
        </View>
      </ScrollView>

      {/* Bottom action bar */}
      <View style={styles.bottomBar}>{actionArea}</View>
    </View>
  );
}
